import { generateRandomHex, request } from '@nextgen-web-framework/core';
import type { RequestOptions } from '@nextgen-web-framework/core';
import { FulfillmentApi } from '@commerce-fulfillment-webcomponents/sdk';
import type { FulfillmentTypeCollectionResponse } from '@commerce-fulfillment-webcomponents/sdk';
import type {
  ProductPlanDetailsAPI,
  CreateProfileAPI,
  UpdateProfileAPI,
  GetProfileAPI,
  PlanDetailsAPI,
  GetSubscriptionAPI,
  CreateSubscriptionAPI,
  SubscriptionsAPI,
  SubscriptionProfileListAPI,
  GetProfilePaymentDays,
  CancelSubscriptionAPI,
  PlanCodeDetailsAPI,
  UpdateSubscriptionAPI,
  CreatePlanAPI,
  UpdatePlanAPI,
  GetPlansAPI,
  GetElementsAPI,
  UpdateElementsAPI,
  CustomRequestOptions,
} from '../../types';
import { ApiProjections } from '../../types';
import {
  type PlanResponse,
  type Plans,
  type SubscriptionResponse,
  type Subscriptions,
  type PaymentDays,
  type SubscriptionElements,
  SubscriptionType,
  ProfileResponse,
  Profiles,
} from '../models';
import {
  createProfileReqObj,
  createSubscriptionReqObj,
  getPlanDetailsReqObj,
  getProfileReqObj,
  getSubscriptionReqObj,
  updateProfileReqObj,
  getSubscriptionProfileListReqObj,
  PlanId,
  ProfileId,
  SubscriptionId,
  updateSubscriptionReqObj,
  createPlanReqObj,
  updatePlanReqObj,
  getElementsReqObj,
  getPaymentDayReqObj,
  getPlansReqObj,
  getSubscriptionsReqObj,
  updateElementsReqObj,
  ElementId,
  ErrorMsg,
} from '../config';
import { removeBlankAttributes } from '../../transformers';

export const subscriptionApi = {
  async getPlanDetailsByProductId(
    { productId, projections = ApiProjections.DETAILED }: ProductPlanDetailsAPI,
    options?: CustomRequestOptions,
  ): Promise<Plans> {
    if (!productId) {
      throw ErrorMsg;
    }

    const requestObj = getPlansReqObj();
    requestObj.params = {
      projections,
      productId,
    };

    return await handleRequest<Plans>(requestObj, undefined, options);
  },

  async createSubscriptionProfile(
    { payload }: CreateProfileAPI,
    options?: CustomRequestOptions,
  ): Promise<ProfileResponse> {
    const requestObj = createProfileReqObj();
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest<ProfileResponse>(requestObj, true, options);
  },

  async updateSubscriptionProfile(
    { profileId, payload }: UpdateProfileAPI,
    options?: CustomRequestOptions,
  ): Promise<ProfileResponse> {
    const requestObj = updateProfileReqObj();
    requestObj.url = requestObj.url.replace(ProfileId, profileId);
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest<ProfileResponse>(requestObj, true, options);
  },

  async getSubscriptionProfile(
    { profileId, productId, projections = ApiProjections.DETAILED }: GetProfileAPI,
    options?: CustomRequestOptions,
  ): Promise<ProfileResponse> {
    if (!profileId) {
      throw ErrorMsg;
    }

    const requestObj = getProfileReqObj();
    requestObj.url = requestObj.url.replace(ProfileId, profileId);
    if (productId) {
      requestObj.params = {
        productId,
        projections,
      };
    } else {
      requestObj.params = {
        projections,
      };
    }
    return await handleRequest<ProfileResponse>(requestObj, undefined, options);
  },

  async getPlanDetailsByPlanId(
    { planId, projections = ApiProjections.DETAILED }: PlanDetailsAPI,
    options?: CustomRequestOptions,
  ): Promise<PlanResponse> {
    if (!planId) {
      throw ErrorMsg;
    }

    const requestObj = getPlanDetailsReqObj();
    requestObj.url = requestObj.url.replace(PlanId, planId);
    requestObj.params = {
      projections,
    };

    return await handleRequest<PlanResponse>(requestObj, undefined, options);
  },

  async getPlanDetailsByPlanCodes(
    { planCodes, projections = ApiProjections.DETAILED }: PlanCodeDetailsAPI,
    options?: CustomRequestOptions,
  ): Promise<Plans> {
    if (!planCodes) {
      throw ErrorMsg;
    }

    const requestObj = getPlansReqObj();
    requestObj.params = {
      codes: planCodes,
      projections,
    };

    return await handleRequest<Plans>(requestObj, undefined, options);
  },

  async getSubscriptionById(
    { subscriptionId, projections = ApiProjections.DETAILED }: GetSubscriptionAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionResponse> {
    if (!subscriptionId) {
      throw ErrorMsg;
    }

    const requestObj = getSubscriptionReqObj();
    requestObj.url = requestObj.url.replace(SubscriptionId, subscriptionId);
    requestObj.params = {
      projections,
    };

    return await handleRequest<SubscriptionResponse>(requestObj, undefined, options);
  },

  async createSubscription(
    { payload }: CreateSubscriptionAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionResponse> {
    const requestObj = createSubscriptionReqObj();
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest(requestObj, true, options);
  },

  async updateSubscription(
    { subscriptionId, payload }: UpdateSubscriptionAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionResponse> {
    const requestObj = updateSubscriptionReqObj();
    requestObj.body = payload as unknown as BodyInit;
    requestObj.url = requestObj.url.replace(SubscriptionId, subscriptionId);

    return await handleRequest<SubscriptionResponse>(requestObj, true, options);
  },

  async getSubscriptionProfileList(
    {
      accountId,
      userProfileId,
      projections = ApiProjections.DETAILED,
      type = SubscriptionType.Automatic,
      page,
      limit,
    }: SubscriptionProfileListAPI,
    options?: CustomRequestOptions,
  ): Promise<Profiles> {
    const requestObj = getSubscriptionProfileListReqObj();
    const configs = ConfigService.getConfigs();
    const SHOULD_ENABLE_PROFILE_API_CHANGE = configs.NEXT_SOP_PROFILE_API_CHANGE_ENABLED;

    requestObj.params = {
      accountId,
      ...(SHOULD_ENABLE_PROFILE_API_CHANGE && userProfileId ? { userProfileId } : {}),
      projections,
      ...(SHOULD_ENABLE_PROFILE_API_CHANGE && type ? { type } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
    };

    return await handleRequest<Profiles>(requestObj, undefined, options);
  },

  async getAllSubscriptions(
    { projections = ApiProjections.DETAILED, accountId, page = '0', filter }: SubscriptionsAPI,
    options?: CustomRequestOptions,
  ): Promise<Subscriptions> {
    const requestObj = getSubscriptionsReqObj();
    requestObj.params = {
      projections,
      page,
    };
    if (accountId) {
      requestObj.params.accountId = accountId;
    }
    if (filter) {
      requestObj.params.filter = filter;
    }

    return await handleRequest<Subscriptions>(requestObj, undefined, options);
  },

  async cancelSubscription(
    { subscriptionId, payload, projections = ApiProjections.DETAILED }: CancelSubscriptionAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionResponse> {
    const requestObj = updateSubscriptionReqObj();
    requestObj.url = requestObj.url.replace(SubscriptionId, subscriptionId);
    requestObj.body = payload as unknown as BodyInit;
    requestObj.params = {
      projections,
    };

    return await handleRequest<SubscriptionResponse>(requestObj, true, options);
  },

  async getPaymentDays(
    { profileId, projections = ApiProjections.DETAILED }: GetProfilePaymentDays,
    options?: CustomRequestOptions,
  ): Promise<PaymentDays> {
    const requestObj = getPaymentDayReqObj();
    requestObj.url = requestObj.url.replace(ProfileId, profileId);
    requestObj.params = {
      projections,
    };

    return await handleRequest<PaymentDays>(requestObj, undefined, options);
  },

  async createPlan({ payload }: CreatePlanAPI, options?: CustomRequestOptions): Promise<PlanResponse> {
    const requestObj = createPlanReqObj();
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest<PlanResponse>(requestObj, true, options);
  },

  async updatePlan({ planId, payload }: UpdatePlanAPI, options?: CustomRequestOptions): Promise<PlanResponse> {
    const requestObj = updatePlanReqObj();
    requestObj.url = requestObj.url.replace(PlanId, planId);
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest<PlanResponse>(requestObj, true, options);
  },

  async getPlans(
    { page, projections = ApiProjections.DETAILED, ...restProps }: GetPlansAPI,
    options?: CustomRequestOptions,
  ): Promise<Plans> {
    const requestObj = getPlansReqObj();
    requestObj.params = {
      projections,
      page,
      ...removeBlankAttributes(restProps),
    };

    return await handleRequest<Plans>(requestObj, undefined, options);
  },

  async getDeleveryOption(fulfullmentTypeIds?: string): Promise<FulfillmentTypeCollectionResponse> {
    const baseUrl = ApiConfigService.getBaseUrl();
    const res: FulfillmentTypeCollectionResponse = await FulfillmentApi.getFulfillmentTypes({
      baseUrl: baseUrl,
      fulfullmentTypeIds: fulfullmentTypeIds,
    });
    return res;
  },

  async getSubscriptionElements(
    { subscriptionId, projections = ApiProjections.DETAILED }: GetElementsAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionElements> {
    const requestObj = getElementsReqObj();

    requestObj.params = {
      subscriptionId,
      projections,
    };

    return await handleRequest<SubscriptionElements>(requestObj, undefined, options);
  },

  async updateSubscriptionElements(
    { elementId, payload }: UpdateElementsAPI,
    options?: CustomRequestOptions,
  ): Promise<SubscriptionElements> {
    const requestObj = updateElementsReqObj();
    requestObj.url = requestObj.url.replace(ElementId, elementId);
    requestObj.body = payload as unknown as BodyInit;

    return await handleRequest<SubscriptionElements>(requestObj, true, options);
  },
};

async function handleRequest<T>(
  requestOptions: RequestOptions,
  skipIdempotency = false,
  options?: CustomRequestOptions,
): Promise<T> {
  if (skipIdempotency) {
    requestOptions.headers = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-Idempotency-Key': generateRandomHex(20),
    };
  }

  // update url, if baseURL found
  if (options?.baseURL) {
    requestOptions.url = options.baseURL + requestOptions.url;
  }
  if (options?.headers) {
    requestOptions.headers = { ...requestOptions?.headers, ...options?.headers };
  }

  const response: T = await request(requestOptions, {
    skipIdempotency,
  });

  return Promise.resolve(response);
}

export default subscriptionApi;
